import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, Plus, Edit, Trash2, ArrowLeft, Search, MoreHorizontal, Calendar,
  DollarSign, Loader2, Play, Pause, Clock, CheckCircle, XCircle, TrendingUp,
  CreditCard, AlertCircle, Building2, Banknote, FileText, Wallet, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, differenceInDays, isPast, isToday, addDays } from "date-fns";

const STATUS_CONFIG = {
  active: { label: "Active", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", icon: CheckCircle },
  paused: { label: "Paused", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400", icon: Pause },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: XCircle },
  completed: { label: "Completed", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400", icon: CheckCircle }
};

const FREQUENCY_LABELS = {
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually"
};

export default function RecurringRevenuePage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Mark as Paid Dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentItem, setPaymentItem] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    client_id: "",
    product_service_id: "",
    amount: "",
    currency: "USD",
    frequency: "monthly",
    payment_method: "",
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: "",
    auto_generate_invoice: true,
    invoice_days_before: 0
  });

  // Payment methods - NO defaults, must be configured by user in Settings
  const paymentMethods = companySettings?.payment_methods?.filter(pm => pm.enabled) || [];
  const hasPaymentMethods = paymentMethods.length > 0;

  // Group payment methods by type for enhanced dropdowns
  const groupedPaymentMethods = {
    gateway: paymentMethods.filter(pm => pm.type === 'gateway'),
    bank: paymentMethods.filter(pm => pm.type === 'bank'),
    cash: paymentMethods.filter(pm => pm.type === 'cash'),
    check: paymentMethods.filter(pm => pm.type === 'check'),
    other: paymentMethods.filter(pm => !pm.type || pm.type === 'other')
  };

  // Format payment method display name with bank details
  const formatPaymentMethodName = (method) => {
    if (method.type === 'bank' && method.bank_name) {
      const lastFour = method.account_number_last4 ? ` ****${method.account_number_last4}` : '';
      return `${method.name} (${method.bank_name}${lastFour})`;
    }
    return method.name;
  };

  // Render grouped payment method select content
  const renderPaymentMethodOptions = () => {
    // If no payment methods configured, show message
    if (!hasPaymentMethods) {
      return (
        <SelectItem value="__none__" disabled>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Settings className="h-4 w-4" />
            <span>No payment methods configured - Go to Settings</span>
          </div>
        </SelectItem>
      );
    }

    const hasGroups = Object.values(groupedPaymentMethods).some(group => group.length > 0);
    
    if (!hasGroups) {
      return paymentMethods.map((method) => (
        <SelectItem key={method.id} value={method.id}>
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span>{formatPaymentMethodName(method)}</span>
          </div>
        </SelectItem>
      ));
    }

    return (
      <>
        {groupedPaymentMethods.gateway.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Payment Gateways</SelectLabel>
            {groupedPaymentMethods.gateway.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-purple-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.bank.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Bank Accounts</SelectLabel>
            {groupedPaymentMethods.bank.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  <span>{formatPaymentMethodName(method)}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.cash.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Cash</SelectLabel>
            {groupedPaymentMethods.cash.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-green-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.check.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Check</SelectLabel>
            {groupedPaymentMethods.check.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.other.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Other</SelectLabel>
            {groupedPaymentMethods.other.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-amber-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
      </>
    );
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [itemsRes, clientsRes, productsRes, statsRes, settingsRes] = await Promise.all([
        api.get("/recurring-revenue"),
        api.get("/clients"),
        api.get("/products-services"),
        api.get("/recurring-revenue/summary/stats"),
        api.get("/company-settings").catch(() => ({ data: null }))
      ]);
      setItems(itemsRes.data || []);
      setClients(clientsRes.data || []);
      setProducts(productsRes.data || []);
      setStats(statsRes.data);
      if (settingsRes.data) {
        setCompanySettings(settingsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "MMM dd, yyyy");
  };

  const getNextBillingStatus = (nextBillingDate) => {
    if (!nextBillingDate) return { status: "unknown", label: "-", color: "text-muted-foreground" };
    
    const billingDate = new Date(nextBillingDate);
    const today = new Date();
    const daysUntil = differenceInDays(billingDate, today);
    
    if (isPast(billingDate) && !isToday(billingDate)) {
      return { 
        status: "overdue", 
        label: `Overdue by ${Math.abs(daysUntil)} days`, 
        color: "text-red-600",
        icon: AlertCircle
      };
    } else if (isToday(billingDate)) {
      return { 
        status: "today", 
        label: "Due Today", 
        color: "text-amber-600",
        icon: Clock
      };
    } else if (daysUntil <= 7) {
      return { 
        status: "soon", 
        label: `Due in ${daysUntil} days`, 
        color: "text-amber-600",
        icon: Clock
      };
    } else {
      return { 
        status: "upcoming", 
        label: formatDate(nextBillingDate), 
        color: "text-muted-foreground",
        icon: Calendar
      };
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "",
      description: item.description || "",
      client_id: item.client_id || "",
      product_service_id: item.product_service_id || "",
      amount: item.amount?.toString() || "",
      currency: item.currency || "USD",
      frequency: item.frequency || "monthly",
      payment_method: item.payment_method || "",
      start_date: item.start_date ? format(new Date(item.start_date), "yyyy-MM-dd") : "",
      end_date: item.end_date ? format(new Date(item.end_date), "yyyy-MM-dd") : "",
      auto_generate_invoice: item.auto_generate_invoice ?? true,
      invoice_days_before: item.invoice_days_before || 0
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      client_id: "",
      product_service_id: "",
      amount: "",
      currency: "USD",
      frequency: "monthly",
      payment_method: "",
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: "",
      auto_generate_invoice: true,
      invoice_days_before: 0
    });
    setEditingItem(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.client_id || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        product_service_id: formData.product_service_id || null,
        payment_method: formData.payment_method || null
      };

      if (editingItem) {
        await api.put(`/recurring-revenue/${editingItem.id}`, payload);
        toast.success("Recurring revenue updated");
      } else {
        await api.post("/recurring-revenue", payload);
        toast.success("Recurring revenue created");
      }

      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/recurring-revenue/${deleteConfirm.id}`);
      toast.success("Recurring revenue deleted");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await api.put(`/recurring-revenue/${item.id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleProcessBilling = async (item) => {
    try {
      const response = await api.post(`/recurring-revenue/${item.id}/bill`);
      toast.success(response.data.message || "Billing processed and recorded in transactions");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process billing");
    }
  };

  const openPaymentDialog = (item) => {
    setPaymentItem(item);
    setShowPaymentDialog(true);
  };

  const handleMarkAsPaid = async () => {
    if (!paymentItem) return;
    
    setIsSaving(true);
    try {
      // Process billing which will mark as paid and create transaction
      const response = await api.post(`/recurring-revenue/${paymentItem.id}/bill`);
      toast.success(response.data.message || "Payment recorded successfully");
      setShowPaymentDialog(false);
      setPaymentItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setIsSaving(false);
    }
  };

  const getPaymentMethodName = (methodId) => {
    const method = paymentMethods.find(pm => pm.id === methodId);
    return method?.name || methodId || "-";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-[400px]" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="recurring-revenue-page">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("recurring_revenue", "company") || "Recurring Revenue"}
              </h1>
              <p className="text-xs text-muted-foreground">
                {t("manage_subscriptions", "company") || "Manage subscriptions and retainers"}
              </p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-recurring-button">
            <Plus className="h-4 w-4 mr-2" />
            {t("add_recurring", "company") || "Add Recurring"}
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-mrr">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">MRR</span>
              </div>
              <p className="text-2xl font-bold text-[hsl(var(--ff-primary))]">
                {formatCurrency(stats?.mrr || 0)}
              </p>
              <p className="text-xs text-muted-foreground">{t("mrr", "company") || "Monthly Recurring Revenue"}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-arr">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm">ARR</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(stats?.arr || 0)}</p>
              <p className="text-xs text-muted-foreground">{t("arr", "company") || "Annual Recurring Revenue"}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-active">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">{t("active", "company") || "Active"}</span>
              </div>
              <p className="text-2xl font-bold">{stats?.active_items || 0}</p>
              <p className="text-xs text-muted-foreground">{t("active_subscriptions", "company") || "Active subscriptions"}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-upcoming">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">{t("upcoming", "company") || "Upcoming"}</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(stats?.upcoming_billings_amount || 0)}</p>
              <p className="text-xs text-muted-foreground">{stats?.upcoming_billings_count || 0} {t("billings_in_30_days", "company") || "billings in 30 days"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_status", "company") || "All Status"}</SelectItem>
              <SelectItem value="active">{t("active", "company") || "Active"}</SelectItem>
              <SelectItem value="paused">{t("paused", "company") || "Paused"}</SelectItem>
              <SelectItem value="cancelled">{t("cancelled", "company") || "Cancelled"}</SelectItem>
              <SelectItem value="completed">{t("completed", "company") || "Completed"}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {filteredItems.length === 0 ? (
              <div className="py-12 text-center">
                <RefreshCw className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">{t("no_recurring_revenue", "company") || "No recurring revenue items found"}</p>
                <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> {t("add_first_subscription", "company") || "Add your first subscription"}
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Next Billing</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => {
                    const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.active;
                    const StatusIcon = statusConfig.icon;
                    const billingStatus = getNextBillingStatus(item.next_billing_date);
                    const BillingIcon = billingStatus.icon || Calendar;
                    return (
                      <TableRow key={item.id} data-testid={`recurring-row-${item.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.name}</p>
                            {item.description && <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>}
                          </div>
                        </TableCell>
                        <TableCell>{item.client_name || "-"}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(item.amount, item.currency)}</TableCell>
                        <TableCell>{FREQUENCY_LABELS[item.frequency] || item.frequency}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <CreditCard className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{getPaymentMethodName(item.payment_method)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1 ${billingStatus.color}`}>
                            <BillingIcon className="h-3 w-3" />
                            <span className="text-sm font-medium">{billingStatus.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.is_paid ? (
                            <Badge variant="outline" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Paid
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusConfig.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`recurring-menu-${item.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(item)}>
                                <Edit className="h-4 w-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              {item.status === "active" && !item.is_paid && (
                                <DropdownMenuItem onClick={() => openPaymentDialog(item)}>
                                  <CheckCircle className="h-4 w-4 mr-2" /> Mark as Paid
                                </DropdownMenuItem>
                              )}
                              {item.status === "active" && (
                                <DropdownMenuItem onClick={() => handleProcessBilling(item)}>
                                  <Play className="h-4 w-4 mr-2" /> Process Billing Now
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {item.status === "active" && (
                                <DropdownMenuItem onClick={() => handleStatusChange(item, "paused")}>
                                  <Pause className="h-4 w-4 mr-2" /> Pause
                                </DropdownMenuItem>
                              )}
                              {item.status === "paused" && (
                                <DropdownMenuItem onClick={() => handleStatusChange(item, "active")}>
                                  <Play className="h-4 w-4 mr-2" /> Resume
                                </DropdownMenuItem>
                              )}
                              {item.status !== "cancelled" && (
                                <DropdownMenuItem onClick={() => handleStatusChange(item, "cancelled")} className="text-destructive">
                                  <XCircle className="h-4 w-4 mr-2" /> Cancel
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => setDeleteConfirm(item)}>
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Recurring Revenue" : "New Recurring Revenue"}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? "Update subscription or retainer details" : "Add a new subscription or retainer"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Monthly SEO Retainer"
                data-testid="recurring-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add details (optional)"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                  <SelectTrigger data-testid="recurring-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Product/Service</Label>
                <Select value={formData.product_service_id || "none"} onValueChange={(v) => setFormData({ ...formData, product_service_id: v === "none" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="recurring-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                  <SelectTrigger data-testid="recurring-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={formData.payment_method || "none"} onValueChange={(v) => setFormData({ ...formData, payment_method: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="recurring-payment-select">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {renderPaymentMethodOptions()}
                </SelectContent>
              </Select>
              {!hasPaymentMethods && (
                <Alert className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No payment methods configured. Go to <Link to="/settings" className="underline font-medium">Settings</Link> to add payment methods.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="recurring-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <Label>Auto Generate Invoice</Label>
                <p className="text-xs text-muted-foreground">Automatically create invoice on billing</p>
              </div>
              <Switch
                checked={formData.auto_generate_invoice}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_generate_invoice: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-recurring-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md" data-testid="payment-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              This will process the billing, create a transaction, and generate an invoice.
            </DialogDescription>
          </DialogHeader>

          {paymentItem && (
            <div className="py-4">
              <Card className="bg-muted/50">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Subscription</span>
                    <span className="font-medium">{paymentItem.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Amount</span>
                    <span className="font-bold text-lg">{formatCurrency(paymentItem.amount, paymentItem.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Payment Method</span>
                    <span className="font-medium">{getPaymentMethodName(paymentItem.payment_method)}</span>
                  </div>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground mt-3">
                This will create an income transaction under the "Recurring Revenue" category.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleMarkAsPaid} disabled={isSaving} data-testid="confirm-payment-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
